import { TOrganizationLog } from "admin-app/models";
import { USER_ACTIVITIES_EVENTS } from "admin-app/services/ciq-queries";
import { ColDef, CsvExportParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import { DatePicker } from "components/antd-date-picker";
import { userNameCellRenderer } from "components/cell-renders";
import CiqAgSearch from "components/ciq-ag-search";
import { GridLoadingIndicator } from "components/widgets";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import dayjs from "utils/dayjs-config";
import { useMemo, useRef, useState, useEffect } from "react";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DATE_FORMAT_YYYYMMDD,
  DateFilter,
  DateUtils
} from "utils/dateutils";
import "./user-activity.scss";
import CiqLogPageHeader from "components/ciq-log-page-header";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import Separator from "../../../components/date-range-picker/separator";

function UserActivityDashboard() {
  const gridRef = useRef<AgGridReact<any>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const { RangePicker } = DatePicker;
  const [dateRange, setDateRange] = useState<{
    fromDate: string;
    toDate: string;
  }>({
    fromDate: dayjs()
      .subtract(1, "week")
      .format(`${DATE_FORMAT_YYYYMMDD} 00:00:00`),
    toDate: dayjs(Date.now()).format(`${DATE_FORMAT_YYYYMMDD} 23:59:59`)
  });

  const { data: userActivitiesEventData, loading } = useCIQQuery(
    USER_ACTIVITIES_EVENTS,
    {
      variables: {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate
      }
    }
  );

  const allEvents: any = useMemo(() => {
    if (!userActivitiesEventData) return null;
    const subLevelEvents =
      userActivitiesEventData?.audit_subscription_events || [];
    const projLevelEvents = userActivitiesEventData?.audit_project_events || [];
    const projFeatureEvents =
      userActivitiesEventData?.audit_project_feature_events || [];

    const combinedEvents = [
      ...subLevelEvents,
      ...projLevelEvents,
      ...projFeatureEvents
    ].sort((e1, e2) => (e1 > e2 ? -1 : 1));

    gridRef.current?.api?.setGridOption("loading", false);
    return combinedEvents;
  }, [userActivitiesEventData]);

  const onFirstDataRendered = () => {
    if (allEvents) {
      gridRef?.current?.api?.setFilterModel({
        filterType: "set",
        values: ["PROJECT_USER_LOGIN", "SUBSCRIPTION_USER_LOGIN"]
      });
    }
  };

  const getFormattedValues = (params: any) => {
    const { value } = params;

    // if (column.colId === "user") {
    //   try {
    //     return `${value}\n${node?.data?.created_by_user?.email}`;
    //   } catch (ex) {
    //     return "";
    //   }
    // }

    return value ?? "";
  };

  const getParams: () => CsvExportParams = () => ({
    columnKeys: gridRef.current?.api
      ?.getAllGridColumns()
      ?.filter((col) => {
        return col.isVisible();
      })
      .map((col) => col.getColId()),
    processCellCallback(params: any): string {
      return getFormattedValues(params);
    },
    fileName: "User Activity log"
  });

  const exportLogAsCSV = () => {
    gridRef.current?.api?.exportDataAsCsv(getParams());
  };

  const columnDefs: ColDef[] = [
    {
      colId: "user",
      headerName: "User Details",
      filter: true,
      cellRenderer: userNameCellRenderer,
      valueGetter: ({ data }) => {
        const userInfo = data?.created_by_user ?? data?.user_created;
        const fullName = `${userInfo?.first_name ?? ""} ${
          userInfo?.last_name ?? ""
        }`;
        return fullName;
      }
    },
    {
      colId: "project",
      field: "project.name",
      headerName: "Project Name",
      valueGetter: ({ data }) => {
        if (data?.project) {
          return data?.project?.name;
        }
        return "";
      },
      filter: true
    },
    {
      colId: "subscription",
      field: "subscription",
      headerName: "Subscription Name",
      valueGetter: ({ data }) => {
        if (data?.project) {
          return data?.project?.owner_subscription?.name;
        }
        return data?.subscription?.name;
      },
      filter: true
    },
    {
      colId: "event_date",
      field: "event_date",
      headerName: "Event Date",
      valueGetter: ({ data }) =>
        DateUtils.format(data.created_at, DATE_FORMAT_MMDDYYYY_HHMM_A),
      comparator: (value1, value2) =>
        DateFilter.customComparator(
          value1,
          value2,
          DATE_FORMAT_MMDDYYYY_HHMM_A
        ),
      tooltipValueGetter: ({ value }) => value
    },
    {
      colId: "event",
      field: "event",
      headerName: "Event",
      sortable: true,
      filter: true,
      valueGetter: ({ data }) => {
        return data?.event_log?.event?.name;
      }
    },
    {
      colId: "organization",
      headerName: "Organization Name",
      filter: true,
      sortingOrder: ["asc", "desc"],
      valueGetter: ({ data }) => {
        if (data?.project) {
          const organizations =
            data?.project?.owner_subscription.organization_subscriptions || [];
          if (!organizations?.length) return "";
          return organizations[0].organization?.name;
        }
        const organizations =
          data?.subscription?.organization_subscriptions || [];
        if (!organizations?.length) return "";
        return organizations[0].organization?.name;
      }
    }
  ];

  const defaultColDef = useMemo((): ColDef => {
    return {
      sortable: true,
      filter: false,
      resizable: true,
      sortingOrder: ["asc", "desc"],
      cellClass: "cellClass",
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const headerItems = [
    <RangePicker
      key="date-range"
      separator={Separator()}
      defaultValue={[dayjs(dateRange.fromDate), dayjs(dateRange.toDate)]}
      onChange={(range: any) => {
        if (range && range?.length === 2) {
          gridRef.current?.api?.setGridOption("loading", true);
          setDateRange({
            fromDate: range[0].format(`${DATE_FORMAT_YYYYMMDD} 00:00:00`),
            toDate: range[1].format(`${DATE_FORMAT_YYYYMMDD} 23:59:59`)
          });
        }
      }}
      data-testid="user-activity-date-range-picker"
    />,
    <CiqAgSearch
      key="search"
      placeholder="Search Activities"
      gridRef={gridRef}
      data-testid="user-activity-search-input"
    />,
    <Button
      key="export"
      type="primary"
      disabled={(allEvents && allEvents?.length === 0) || loading}
      onClick={exportLogAsCSV}
      data-testid="user-activity-dashboard-button"
    >
      Export
    </Button>
  ];

  return (
    <div className="flex flex-col h-full">
      <CiqLogPageHeader
        {...{
          entityName: "User Activity",
          gridRef,
          items: headerItems
        }}
      />

      <div className="grow flex-col ag-theme-alpine">
        <AgGridReact<TOrganizationLog>
          ref={gridRef}
          rowData={allEvents}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={36}
          loadingOverlayComponent={GridLoadingIndicator}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          onFirstDataRendered={onFirstDataRendered}
        />
      </div>
    </div>
  );
}
export default UserActivityDashboard;
